/**
 * useSiteFeatures Hook
 *
 * Owns the feature flags for one site: reads what is stored, holds the operator's
 * edits, and saves only the flags that changed.
 *
 * Edits are held locally until saved rather than written on every toggle, because
 * a flag takes effect for every player at the site on their next request.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track } from '../../analytics';
import type { SiteFeaturesUpdatedAttributes } from '../../analytics';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import {
  fetchSiteFeatures,
  patchSiteFeatures,
  type SiteFeaturesApiClientConfig,
  type SiteFeaturesFailure,
} from '../api/siteFeaturesApiClient';
import {
  DEFAULT_SITE_FEATURES,
  type SiteFeatureFlag,
  type SiteFeatures,
  type SiteFeaturesState,
} from '../models/domainModels';
import { toSiteFeaturesState } from '../models/siteFeaturesAdapter';
import { buildFeaturesPatch, changedFlags as diffFlags } from '../utils/featuresPatch';

/**
 * Why the flags could not be read or written on authentication grounds.
 *
 * `expired` is remedied by signing in again; `forbidden` is not.
 */
export type SiteFeaturesAuthError = 'expired' | 'forbidden';

export interface UseSiteFeaturesResult {
  /** What is stored, or null before the first successful read. */
  readonly stored: SiteFeaturesState | null;
  /** The flags as currently shown, including unsaved edits. */
  readonly features: SiteFeatures;
  readonly changedFlags: readonly SiteFeatureFlag[];
  readonly hasUnsavedChanges: boolean;
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  /** The site has no configuration, so it has no flags to set. */
  readonly isNotFound: boolean;
  readonly error: string | null;
  readonly authError: SiteFeaturesAuthError | null;
  readonly setFeature: (flag: SiteFeatureFlag, enabled: boolean) => void;
  readonly save: () => Promise<void>;
  readonly discard: () => void;
  readonly reload: () => Promise<void>;
}

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

export function useSiteFeatures(siteId: string | undefined): UseSiteFeaturesResult {
  const [stored, setStored] = useState<SiteFeaturesState | null>(null);
  const [edited, setEdited] = useState<SiteFeatures>(DEFAULT_SITE_FEATURES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<SiteFeaturesAuthError | null>(null);

  const { getAccessToken } = useAuth();

  // A ref so the load effect does not re-run when this callback changes identity.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Reset on mount as well as cleared on unmount, so a StrictMode remount does not
  // leave the hook unable to set state.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // A ref rather than `isSaving`, which has not landed by the time a second click can.
  const savingRef = useRef(false);

  const apiConfig = useCallback(
    (): SiteFeaturesApiClientConfig => ({
      apiHost: getAppConfig().apiHost,
      getAccessToken: getAccessTokenRef.current,
    }),
    []
  );

  const clearFailures = useCallback(() => {
    setError(null);
    setAuthError(null);
    setIsNotFound(false);
  }, []);

  const applyFailure = useCallback((failure: SiteFeaturesFailure) => {
    switch (failure.status) {
      case 'not_found':
        setIsNotFound(true);
        break;
      case 'unauthorized':
        setAuthError('expired');
        break;
      case 'forbidden':
        setAuthError('forbidden');
        break;
      case 'invalid':
      case 'error':
        setError(failure.message);
        break;
      default: {
        const exhaustive: never = failure;
        return exhaustive;
      }
    }
  }, []);

  /** Adopt a stored state as both the baseline and what is shown. */
  const adopt = useCallback((state: SiteFeaturesState) => {
    setStored(state);
    setEdited(state.features);
  }, []);

  const load = useCallback(async () => {
    if (!siteId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    clearFailures();

    try {
      const result = await fetchSiteFeatures(siteId, apiConfig());
      if (!mountedRef.current) return;

      if (result.status === 'ok') {
        adopt(toSiteFeaturesState(result.data));
      } else {
        setStored(null);
        applyFailure(result);
      }
    } catch (err) {
      if (mountedRef.current) setError(errorMessage(err, 'Failed to load features'));
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [siteId, apiConfig, clearFailures, applyFailure, adopt]);

  useEffect(() => {
    void load();
  }, [load]);

  const changedFlags = useMemo(
    () => (stored ? diffFlags(stored.features, edited) : []),
    [stored, edited]
  );

  const setFeature = useCallback((flag: SiteFeatureFlag, enabled: boolean) => {
    setEdited((previous) => ({ ...previous, [flag]: enabled }));
  }, []);

  const discard = useCallback(() => {
    if (stored) setEdited(stored.features);
  }, [stored]);

  const save = useCallback(async () => {
    if (!siteId || !stored || changedFlags.length === 0) return;
    if (savingRef.current) return;

    savingRef.current = true;
    setIsSaving(true);
    clearFailures();

    try {
      const result = await patchSiteFeatures(
        siteId,
        buildFeaturesPatch(stored.features, edited),
        apiConfig()
      );
      if (!mountedRef.current) return;

      if (result.status === 'ok') {
        const state = toSiteFeaturesState(result.data);
        adopt(state);

        const attributes: SiteFeaturesUpdatedAttributes = {
          SiteId: siteId,
          ChangedFlags: changedFlags,
          Features: state.features,
        };
        track('Pocket Admin Site Features Updated', attributes);
      } else {
        // Edits stay in place: a rejected PATCH writes nothing, so they still
        // describe what the operator was trying to save.
        applyFailure(result);
      }
    } catch (err) {
      if (mountedRef.current) setError(errorMessage(err, 'Failed to save features'));
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setIsSaving(false);
    }
  }, [siteId, stored, edited, changedFlags, apiConfig, clearFailures, applyFailure, adopt]);

  return useMemo(
    () => ({
      stored,
      features: edited,
      changedFlags,
      hasUnsavedChanges: changedFlags.length > 0,
      isLoading,
      isSaving,
      isNotFound,
      error,
      authError,
      setFeature,
      save,
      discard,
      reload: load,
    }),
    [
      stored,
      edited,
      changedFlags,
      isLoading,
      isSaving,
      isNotFound,
      error,
      authError,
      setFeature,
      save,
      discard,
      load,
    ]
  );
}
